/**
 * ProposalKanbanCard - Card de proposta no Kanban de Propostas
 *
 * Somente leitura (sem arrastar e soltar): o card muda de coluna sozinho
 * quando a situação muda no backend (Realtime).
 *
 * @version 1.1.0
 * RELEASE: 2026-08-25 v1.1.0 - Tipografia padronizada com o ContractKanbanCard: número em
 *          `font-mono text-[11px]` com ícone, título `text-xs leading-tight line-clamp-2` +
 *          tooltip nativo, cliente `text-[11px]`, negócio/rodapé `text-[10px]`
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 1 — Kanban de Propostas)
 */
import React from 'react';
import { Briefcase, Calendar, Eye, FileText, Repeat } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';
import type { KanbanProposal } from '@/hooks/useProposalKanbanColumn';
import { ProposalRowActions } from '@/components/crm/proposals/ProposalRowActions';

interface ProposalKanbanCardProps {
  proposal: KanbanProposal;
  /** Abre o modal de detalhes da proposta */
  onOpen: (proposalId: string) => void;
  /** Abre o negócio vinculado */
  onOpenDeal?: (dealId: string) => void;
  /** Callback após ação do menu */
  onActionComplete?: () => void;
}

/** Formata valores monetários em BRL */
function formatCurrency(value: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency || 'BRL',
  }).format(value || 0);
}

/** Formata data curta (dd/mm/aaaa) */
function formatDate(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString('pt-BR');
}

export function ProposalKanbanCard({
  proposal,
  onOpen,
  onOpenDeal,
  onActionComplete,
}: ProposalKanbanCardProps) {
  // Data mais relevante conforme a situação atual
  const relevantDate =
    formatDate(proposal.accepted_at) ||
    formatDate(proposal.rejected_at) ||
    formatDate(proposal.sent_at) ||
    formatDate(proposal.created_at);

  return (
    <Card
      className={cn(
        'p-2.5 cursor-pointer transition-colors hover:border-primary/40 hover:bg-accent/30',
        proposal.is_expired && 'border-orange-500/40',
      )}
      onClick={() => onOpen(proposal.id)}
    >
      {/* Linha 1 — número + tipo + ações */}
      <div className="flex items-start justify-between gap-1.5">
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
          <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="font-mono text-[11px] text-muted-foreground">
            {proposal.proposal_number}
          </span>
          {proposal.version_number > 1 && (
            <span className="text-[10px] text-muted-foreground">v{proposal.version_number}</span>
          )}
          {proposal.proposal_type_name && (
            <Badge
              variant="outline"
              className="h-4 px-1.5 text-[10px] border-0"
              style={{
                backgroundColor: `${proposal.proposal_type_color || '#64748b'}1a`,
                color: proposal.proposal_type_color || undefined,
              }}
            >
              {proposal.proposal_type_name}
            </Badge>
          )}
        </div>

        <ProposalRowActions
          proposal={proposal}
          onViewDetails={() => onOpen(proposal.id)}
          onViewDeal={
            proposal.deal_id && onOpenDeal ? () => onOpenDeal(proposal.deal_id!) : undefined
          }
          onActionComplete={onActionComplete}
        />
      </div>

      {/* Linha 2 — título da proposta (tooltip nativo com o texto completo) */}
      <p
        className="text-xs font-medium leading-tight line-clamp-2 break-words mt-1"
        title={proposal.title || proposal.deal_title || undefined}
      >
        {proposal.title || proposal.deal_title || 'Proposta'}
      </p>

      {/* Linha 3 — cliente */}
      {proposal.contact_name && (
        <div className="flex items-center gap-1.5 mt-1.5 min-w-0">
          <SmartAvatar
            name={proposal.contact_name}
            src={proposal.contact_avatar || undefined}
            size="xs"
          />
          <span className="text-[11px] truncate" title={proposal.contact_name}>
            {proposal.contact_name}
          </span>
        </div>
      )}

      {/* Linha 4 — negócio vinculado */}
      {proposal.deal_title && (
        <div className="flex items-center gap-1.5 mt-1 min-w-0">
          <Briefcase className="h-3 w-3 text-muted-foreground shrink-0" />
          <span
            className="text-[10px] text-muted-foreground truncate"
            title={proposal.deal_title}
          >
            {proposal.deal_title}
          </span>
        </div>
      )}

      {/* Linha 5 — valores */}
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <span className="text-[11px] font-medium whitespace-nowrap">
          {formatCurrency(proposal.total, proposal.currency)}
        </span>
        {proposal.mrr_total > 0 && (
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Repeat className="h-3 w-3" />
            {formatCurrency(proposal.mrr_total, proposal.currency)}/mês
          </span>
        )}
      </div>

      {/* Rodapé — metadados */}
      <div className="flex items-center justify-between gap-2 mt-1.5 pt-1.5 border-t">
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground min-w-0">
          {relevantDate && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {relevantDate}
            </span>
          )}
          <span className="flex items-center gap-1">
            <FileText className="h-3 w-3" />
            {proposal.items_count}
          </span>
          {proposal.first_viewed_at && (
            <span className="flex items-center gap-1 text-blue-600">
              <Eye className="h-3 w-3" />
              vista
            </span>
          )}
        </div>

        {proposal.seller_name && (
          <SmartAvatar
            name={proposal.seller_name}
            src={proposal.seller_avatar || undefined}
            size="xs"
          />
        )}
      </div>
    </Card>
  );
}
